/** Gemeinsame Rechtequelle für Listen und neutrale Datensatzverweise (AD-43). */
import type { PermissionArea, PermissionKey } from './catalog.ts';
import { can, type EffectivePermissions } from './resolve.ts';
import { legacyRolePermissions } from './builtin-roles.ts';

export const listPermissionKinds = ['read', 'personal', 'export', 'bulk', 'share', 'watch', `import`, 'ai'] as const;
export type ListPermissionKind = (typeof listPermissionKinds)[number];
export interface ListPermission {
  /** Bereich der Liste im Katalog. */
  area: PermissionArea;
  /** Teil-Umfang für Export und Massenbearbeitung (Liste „Aufgaben“, Story 23.17). */
  scope?: 'tasks';
  /**
   * Zusätzliches Recht für eigene Ansichten und Vorlieben. Versand (`shipping_queue`): Ansichten filtern nach Auftrag
   * und Kunde (#1725) – der Packplatz speichert heute keine Ansichten (Ticket 1.95).
   */
  personal?: PermissionKey;
  /**
   * Arten, die jede angemeldete Person nutzen darf (Recht `time:own`, das jede Rolle hat). Sendungen (`shipments`,
   * Story 11.41, [RUDOLF 02.11.2026]: Versand › Sendungen für jede Rolle): lesen, eigene Ansichten, Export; Auftrag und
   * Kunde sperrt das Listen-Register je Spalte (`ListColumn.requires`).
   */
  everyone?: readonly ListPermissionKind[];
}

/**
 * Bereich je Liste des Listen-Baukastens. Eine Liste ohne Eintrag sieht nur der Admin (Sperre im Zweifel);
 * `role-equality.test.ts` (Katalog-Test) verlangt für jede Liste aus `listKeys()` einen Eintrag.
 */
export const LIST_PERMISSIONS: Readonly<Record<string, ListPermission>> = Object.freeze({
  customers: { area: 'customers' },
  articles: { area: 'articles' },
  gtins: { area: 'articles' },
  bin_locations: { area: 'articles' },
  stock: { area: 'articles' },
  stock_valuation: { area: 'articles' },
  stock_movements: { area: 'articles' },
  stocktakes: { area: 'articles' },
  stocktake_lines: { area: 'articles' },
  recount_marks: { area: 'articles' },
  custom_field_values: { area: 'articles' },
  margin_reviews: { area: 'purchasing' },
  incoming_payments: { area: 'payments' },
  // Finanzen › Zahlungen › „Braucht dich“ (Story 13.8): lesen mit dem Bereich Zahlungen; bestätigen braucht payments:book.
  payment_suggestions: { area: 'payments' },
  orders: { area: 'orders' },
  quotes: { area: 'orders' },
  sold_positions: { area: 'orders' },
  order_drafts: { area: 'orders' },
  order_backlog: { area: 'orders' },
  blanket_orders: { area: 'orders' },
  // Abholung: Auftragsdetail und Preise – nicht der Packplatz (Story Selbstabholung).
  pickup_ready: { area: 'orders' },
  suppliers: { area: 'purchasing' },
  purchase_orders: { area: 'purchasing' },
  reorder_proposals: { area: 'purchasing' },
  supplier_prices: { area: 'purchasing' },
  shipping_queue: { area: 'shipping', personal: 'orders:read' },
  shipments: { area: 'shipping', everyone: ['read', 'personal', 'export'] },
  returns: { area: 'returns' },
  documents: { area: 'documents' },
  tasks: { area: 'tasks', scope: 'tasks' },
  // Einstellungen › Drucken › Druckverlauf (Story 27.6): wie die Einstellungen (Grundzugang); nicht Packplatz/Mitarbeiter.
  label_prints: { area: 'start' },
});

/** Aktuelles Leserecht einer Liste; unbekannte Listen bleiben gesperrt. */
export function listReadPermission(list: string): PermissionKey | null {
  const entry = Object.hasOwn(LIST_PERMISSIONS, list) ? LIST_PERMISSIONS[list] : undefined;
  return entry ? entry.everyone?.includes('read') ? 'time:own' : `${entry.area}:read` : null;
}

/** Rechte, die eine Listenspalte verlangen kann (`ListColumn.requires`). */
export const LIST_COLUMN_RIGHTS = ['customers', 'orders'] as const;
export type ListColumnRight = (typeof LIST_COLUMN_RIGHTS)[number];

/** Katalog-Schlüssel hinter den Prüffunktionen (Verzeichnis für Story 28.5). */
export const LIST_RIGHT_PERMISSIONS: Readonly<Record<ListColumnRight | 'orderDetail', PermissionKey>> = Object.freeze({
  customers: 'customers:read',
  orders: 'orders:read',
  orderDetail: 'orders:read',
});


/** Übergang für alte interne Aufrufer; HTTP-Anfragen geben immer effektive Rechte weiter. */
export type ListPermissionViewer = EffectivePermissions | string | null | undefined;
export function listViewerPermissions(viewer: ListPermissionViewer): EffectivePermissions | null {
  return typeof viewer === 'string' ? legacyRolePermissions(viewer) : viewer ?? null;
}
export function canViewCustomers(viewer: ListPermissionViewer): boolean {
  const effective = listViewerPermissions(viewer);
  return effective !== null && can(effective, 'customers:read');
}

/**
 * Enge Versand-Arbeitsprojektion (Change Rollen/Rechte §4.2 „Bearbeiten, heutiger Umfang“, Story 11.41):
 * Versand bearbeiten braucht Kunde und Kundennummer in Pack-/Sendungsliste, aber keinen Kundenstamm oder Kundenlink.
 * Reines Versandlesen gewährt diese Projektion nicht. Keine Ableitung aus einem Rollennamen.
 */
export function canReadShippingCustomerColumn(permissions: EffectivePermissions | null, list: string, column: string): boolean {
  const [module, field, ...rest] = column.split('.');
  return permissions !== null && (list === 'shipments' || list === 'shipping_queue')
    && module === 'orders' && rest.length === 0 && (field === 'customer_name' || field === 'customer_number') && can(permissions, 'shipping:write');
}

export function canViewOrders(viewer: ListPermissionViewer): boolean {
  const effective = listViewerPermissions(viewer);
  return effective !== null && can(effective, 'orders:read');
}
export const canOpenOrderDetail = canViewOrders;
export function canViewListColumnRight(viewer: ListPermissionViewer, right: ListColumnRight): boolean {
  return right === 'customers' ? canViewCustomers(viewer) : canViewOrders(viewer);
}

const RECORD_READ_AREAS: Readonly<Record<string, PermissionArea>> = {
  customer: 'customers', order: 'orders', order_draft: 'orders', blanket_order: 'orders', quote: 'orders',
  article: 'articles', bin_location: 'articles', stocktake: 'articles', recount_mark: 'articles', gtin: 'articles',
  supplier: 'purchasing', supplier_price: 'purchasing', purchase_order: 'purchasing', goods_receipt: 'purchasing',
  document: 'documents', invoice: 'documents', return: 'returns', shipment: 'shipping', shipping_label: 'shipping', delivery_note_batch_print: 'shipping', mass_edit_sentence: 'ai', datev_batch: 'datev',
  bank_payment: 'payments', checkout_payment: 'payments', payment: 'payments', incoming_payment: 'payments',
  task: 'tasks', task_project: 'tasks', task_recurrence: 'tasks', case: 'start',
  reorder_proposal: 'purchasing', task_quick_entry: 'tasks', task_description_draft: 'tasks', app_user: 'start', mass_edit: 'tasks', change_set: 'start', label_template: 'labels', label_print: 'labels',
};

/** Bereiche von Datensatzverweisen; Unbekanntes wird für eingeschränkte Leser gesperrt. */
export function recordReadPermission(type: string): PermissionKey | null {
  if (type === 'system_notice') return 'admin:restore';
  const area = Object.hasOwn(RECORD_READ_AREAS, type) ? RECORD_READ_AREAS[type] : undefined;
  return area ? `${area}:read` : null;
}

/** Alle Datensatzarten mit eigenem Leserecht aus `recordReadPermission` (eine Quelle, z. B. für Klärfall-Bezüge). */
export function recordReadTypes(): readonly string[] {
  return ['system_notice', ...Object.keys(RECORD_READ_AREAS)];
}

/**
 * Enge Etiketten-Projektion (HS-Entscheidung 04.11.2026 zu 28.5; abgenommenes Etikettenbearbeiten 27.x, Plan 28.5:
 * „erlaubte Etiketten-/Versandprojektionen bleiben unverändert“): Nur auf Etikettenwegen (Feldkatalog, Muster,
 * Druckwerte) und nur für die Artikel- und Lagerplatzliste gilt `labels:read` wie `articles:read`. Geldfelder bleiben
 * über `labelFieldVisible` an `prices:sales` gebunden, andere Bereiche (z. B. Einkauf) bleiben gesperrt. Nie für die
 * allgemeine Artikelliste, Suche oder Export; keine Ableitung aus einem Rollennamen.
 */
export function labelProjectionPermissions(permissions: EffectivePermissions | null): EffectivePermissions | null {
  if (permissions === null || !can(permissions, 'labels:read') || can(permissions, 'articles:read')) return permissions;
  return Object.freeze({ ...permissions, keys: Object.freeze([...permissions.keys, 'articles:read' as PermissionKey]) });
}

const CUSTOMER_AUDIT_FIELD = /^(customer_(name|number|id|comment)|billing_address|shipping_address)$/;

/**
 * Alle Leserechte eines Ereignisses (Zweit-Review #1791): das Recht des auslösenden Bereichs und – bei Kunden- oder
 * Adressfeldern – zusätzlich `customers:read`. Das Kundenrecht ersetzt das Bereichsrecht nie. `null` = gesperrt.
 */
export function auditReadPermissions(entry: { command: string; entityType: string; fields?: readonly string[] }): PermissionKey[] | null {
  const area = auditReadPermission(entry);
  if (area === null) return null;
  return entry.fields?.some((field) => CUSTOMER_AUDIT_FIELD.test(field)) && area !== 'customers:read' ? [area, 'customers:read'] : [area];
}

/** Recht des auslösenden Bereichs eines Ereignisses (ohne Feldschutz – dafür `auditReadPermissions`). */
export function auditReadPermission(entry: { command: string; entityType: string; fields?: readonly string[] }): PermissionKey | null {
  const [module] = entry.command.split('.');
  if (module === 'billing' && /payment|bank|paypal|checkout|allocation/i.test(entry.command)) return 'payments:read';
  // DATEV-Stapel (Story 16.9) wie Finanzen › DATEV-Export nur mit `datev:read`, nicht mit dem allgemeinen Forderungsrecht.
  if (module === 'billing' && (/datev/i.test(entry.command) || entry.entityType === 'datev_batch')) return 'datev:read';
  const areas: Readonly<Record<string, PermissionArea>> = {
    customers: 'customers', orders: 'orders', quotes: 'orders', catalog: 'articles', inventory: 'articles',
    purchasing: 'purchasing', shipping: 'shipping', returns: 'returns', documents: 'documents',
    billing: 'receivables', mail: 'documents', collab: 'tasks', ai: 'ai', assistant: 'ai',
    settings: 'start', platform: 'start', integrations: 'start', loopback: 'start', reporting: 'reports', staff: 'start',
    rules: 'start', shopware: 'start', ebay: 'start', dhl: 'shipping', dhl_express: 'shipping', paypal: 'payments', ebics: 'payments', taxrules: 'start', migration: 'start',
  };
  const area = module && Object.hasOwn(areas, module) ? areas[module] : undefined;
  return area ? `${area}:read` : recordReadPermission(entry.entityType);
}

/** Rechte des Zielbereichs einer gespeicherten Meldung, niemals vom Browser vorgegeben. */
export function noticeReadPermission(href: string | null): PermissionKey | null {
  if (href === null) return 'tasks:read';
  const path = href.split(/[?#]/)[0] ?? '';
  const areas: readonly [RegExp, PermissionKey][] = [
    [/^\/kunden(?:\/|$)/, 'customers:read'], [/^\/(auftraege|angebote)(?:\/|$)/, 'orders:read'],
    [/^\/(artikel|lager)(?:\/|$)/, 'articles:read'], [/^\/einkauf(?:\/|$)/, 'purchasing:read'],
    [/^\/versand(?:\/|$)/, 'shipping:read'], [/^\/retouren(?:\/|$)/, 'returns:read'],
    [/^\/finanzen\/zahlungen(?:\/|$)/, 'payments:read'], [/^\/finanzen(?:\/|$)/, 'receivables:read'],
    [/^\/belege(?:\/|$)/, 'documents:read'], [/^\/berichte(?:\/|$)/, 'reports:read'],
    [/^\/(aufgaben|wiedervorlagen)(?:\/|$)/, 'tasks:read'], [/^\/zeiterfassung(?:\/|$)/, 'time:own'],
    [/^\/(einstellungen|klaerfaelle)(?:\/|$)/, 'start:read'], [/^\/$/, 'start:read'],
  ];
  return areas.find(([pattern]) => pattern.test(path))?.[1] ?? null;
}
